import { useEffect, useReducer } from 'react'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { isValidDate } from '@quickmd-llc/frontend-utils'
import { formatTime } from '../utils'

// Where an order's latest fulfillment failure stands, as told by MQTT. Client-only: it lives in the
// React Query cache under a key no endpoint uses, so components read it reactively and the order list
// and detail stay the source of truth for everything else. Only the events this tab saw feed it.
const ATTEMPT_QUERY_ROOT = 'pharmacyOrderFulfillmentAttempt'

// How long past its scheduled time a retrying entry stays believable: an attempt that is due can take
// this long to report back, so a retrying entry older than that was either superseded by an event that
// never arrived or has finished silently.
const ATTEMPT_WINDOW_MS = 15 * 60 * 1000

// The longest delay setTimeout honours; a longer one fires immediately.
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1

const FulfillmentAttemptSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('retrying'),
    attemptNumber: z.number().nullable(),
    nextAttemptAtUtc: z.string().nullable(),
    recordedAt: z.number(),
  }),
  z.object({ kind: z.literal('failed'), message: z.string().nullable() }),
])

export type FulfillmentAttempt = z.infer<typeof FulfillmentAttemptSchema>

function attemptKey(orderId: string | null) {
  return [ATTEMPT_QUERY_ROOT, orderId] as const
}

// Cache contents are `unknown` (and can arrive from another tab), so anything else parses to "none".
function parseFulfillmentAttempt(data: unknown): FulfillmentAttempt | null {
  const parsed = FulfillmentAttemptSchema.safeParse(data)
  return parsed.success ? parsed.data : null
}

/**
 * An entry nobody observes would be garbage collected after the default 5 minutes, long before the
 * later retries (up to an hour out) fire. Defaults apply when a query is built, and entries that
 * arrive from another tab (broadcast) are built by this tab's client without going through a write
 * here, so every tab registers them once at startup, before any entry can arrive.
 */
export function registerAttemptQueryDefaults(queryClient: QueryClient): void {
  queryClient.setQueryDefaults([ATTEMPT_QUERY_ROOT], { gcTime: Infinity, staleTime: Infinity })
}

function writeAttempt(queryClient: QueryClient, orderId: string, attempt: FulfillmentAttempt | null): void {
  queryClient.setQueryData(attemptKey(orderId), attempt)
}

export function recordRetryingAttempt(
  queryClient: QueryClient,
  orderId: string,
  details: { attemptNumber: number | null; nextAttemptAtUtc: string | null },
  recordedAt: number = Date.now(),
): void {
  writeAttempt(queryClient, orderId, { kind: 'retrying', ...details, recordedAt })
}

export function recordFailedAttempt(queryClient: QueryClient, orderId: string, message: string | null): void {
  writeAttempt(queryClient, orderId, { kind: 'failed', message })
}

export function clearFulfillmentAttempt(queryClient: QueryClient, orderId: string): void {
  writeAttempt(queryClient, orderId, null)
}

export type FailedFulfillmentAttempt = Extract<FulfillmentAttempt, { kind: 'failed' }>

/**
 * Drops a stale final-failure message once the pharmacist tries again; leaves a retrying entry alone.
 * Returns what it dropped so a request that then fails can put it back.
 */
export function clearFailedFulfillmentAttempt(
  queryClient: QueryClient,
  orderId: string,
): FailedFulfillmentAttempt | null {
  const current = parseFulfillmentAttempt(queryClient.getQueryData(attemptKey(orderId)))
  if (current?.kind !== 'failed') return null
  clearFulfillmentAttempt(queryClient, orderId)
  return current
}

/** Puts a dropped final failure back, unless something newer has been recorded for the order since. */
export function restoreFailedFulfillmentAttempt(
  queryClient: QueryClient,
  orderId: string,
  attempt: FailedFulfillmentAttempt,
): void {
  if (parseFulfillmentAttempt(queryClient.getQueryData(attemptKey(orderId)))) return
  writeAttempt(queryClient, orderId, attempt)
}

// When a retrying entry stops describing the order: the window after its scheduled attempt (or, with
// no usable scheduled time, the window after it was recorded).
function getRetryExpiry(entry: Extract<FulfillmentAttempt, { kind: 'retrying' }>): number {
  const scheduledAt = entry.nextAttemptAtUtc === null ? Number.NaN : Date.parse(entry.nextAttemptAtUtc)
  return (Number.isNaN(scheduledAt) ? entry.recordedAt : scheduledAt) + ATTEMPT_WINDOW_MS
}

/**
 * The entry if it still describes the order, else null. A final failure holds until the order's
 * outcome changes; a retrying entry expires once its window has passed, so a dropped event cannot
 * strand it.
 */
export function getActiveAttempt(entry: FulfillmentAttempt | null, now: number): FulfillmentAttempt | null {
  if (!entry) return null
  if (entry.kind === 'failed') return entry
  return now > getRetryExpiry(entry) ? null : entry
}

/** The order's active attempt state, re-read on every cache change and when a retrying entry expires. */
export function useFulfillmentAttempt(orderId: string | null): FulfillmentAttempt | null {
  const queryClient = useQueryClient()
  const [, rerender] = useReducer((count: number) => count + 1, 0)
  const { data } = useQuery({
    queryKey: attemptKey(orderId),
    // Never fetches (`enabled: false`); present only so a stray refetch hands back what is cached.
    queryFn: (): unknown => queryClient.getQueryData(attemptKey(orderId)) ?? null,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
    // Runs on every render; the timer below is what makes a render happen when the window closes.
    select: (cached: unknown) => getActiveAttempt(parseFulfillmentAttempt(cached), Date.now()),
  })
  const attempt = data ?? null

  // Nothing else renders the row when a retrying entry's window closes, so synchronise with the clock.
  const expiresAt = attempt?.kind === 'retrying' ? getRetryExpiry(attempt) : null
  useEffect(() => {
    if (expiresAt === null) return
    const delay = Math.min(Math.max(expiresAt - Date.now() + 1, 0), MAX_TIMER_DELAY_MS)
    const timer = setTimeout(rerender, delay)
    return () => clearTimeout(timer)
  }, [expiresAt])

  return attempt
}

/** "Attempt 2 failed · retrying at 6:20 PM", leaving out whichever parts the event did not carry. */
export function formatRetryStatus(
  attempt: { attemptNumber: number | null; nextAttemptAtUtc: string | null },
  separator: string,
): string {
  const retryAt =
    attempt.nextAttemptAtUtc !== null && isValidDate(new Date(attempt.nextAttemptAtUtc)) ?
      formatTime(attempt.nextAttemptAtUtc)
    : null
  const retrying = retryAt ? `retrying at ${retryAt}` : 'retrying'
  if (attempt.attemptNumber === null) return `${retrying.charAt(0).toUpperCase()}${retrying.slice(1)}`
  return `Attempt ${attempt.attemptNumber} failed${separator}${retrying}`
}
